import tacHelpers from '../../../../state/tac/tacHelpers';
import productService from '../../../../services/products';
import geometry from '../../../../scene/util/geometry';
import { isFixedRoom } from '../../../../util/room';
import { ITEMS } from '../../../../constants';

function shouldMoveRight(upright, section) {
  return upright.x > section.x;
}

function shouldMoveLeft(upright, section) {
  return upright.x < section.x;
}

function getMoveDirection(from, to, dimension, tac) {
  if (
    (from.id === to.id && !from.logic.extendable) ||
    !productService.isSection(from)
  ) {
    return;
  }

  const diff = to.value - from.width;
  const superSection = tacHelpers.findSuperSection(tac, from);
  const blockers = tacHelpers.getVerticalNeighbours(from, superSection);
  if (blockers.length) {
    /*
    Vertical neighbours (inside same supersection) are considered blockers,
    since it can be very hard to pre-validate width change.

    but, we ignore lagkapten sections that are empty
    */

    const relevant = blockers.filter(
      blocker => blocker.filter.switchable || blocker.items?.length
    );

    if (relevant.length) {
      return;
    }
  }

  const wallMaxX = Math.max(...tac.wall.points.map(point => point.x));

  if (!isFixedRoom()) {
    const space = tacHelpers.getInitialSpace(tac);
    const itemSpace = tacHelpers.getSpaceForItem(space, from);
    if (wallMaxX + diff > itemSpace.width) {
      return;
    }
  }

  const uprights = superSection.filter(item =>
    productService.isType(item, ITEMS.UPRIGHT)
  );

  function getUprightChains(uprights) {
    uprights.sort((a, b) => {
      return a.y - b.y;
    });

    return uprights.reduce((chains, upright) => {
      const chainIndex = chains.findIndex(
        chain => chain.x === upright.x && chain.y + chain.height === upright.y
      );
      const chain = chains[chainIndex];

      if (chain) {
        chain.height = chain.height + upright.height;
        chains[chainIndex] = chain;
      } else {
        chains.push(upright);
      }

      return chains;
    }, []);
  }

  const outsiders = tacHelpers
    .filterTac(
      tac,
      superSection.map(item => item.itemid)
    )
    .items.filter(item => productService.isType(item, ITEMS.UPRIGHT));
  const outsiderChains = getUprightChains(outsiders);

  const movers = uprights
    .filter(upright => shouldMoveRight(upright, from))
    .map(mover => {
      return { ...mover, x: mover.x + diff };
    });

  const blockedRight =
    outsiderChains.some(uprightChain =>
      movers.some(
        mover =>
          /**
           * If collision or jumping past other upright and
           * is encapsulated y-wise by the other upright
           * the selected section will be destroyed, so don't allow
           */
          geometry.collides(uprightChain, mover) ||
          (mover.y >= uprightChain.y &&
            mover.height + mover.y <= uprightChain.height + uprightChain.y &&
            mover.x > uprightChain.x &&
            mover.x - diff < uprightChain.x)
      )
    ) || movers.some(mover => mover.x + mover.width > wallMaxX);
  if (!blockedRight) {
    return 'right';
  } else {
    const movers = uprights
      .filter(upright => shouldMoveLeft(upright, from))
      .map(mover => {
        return { ...mover, x: mover.x - diff };
      });

    const blockedLeft =
      outsiderChains.some(uprightChain =>
        movers.some(
          mover =>
            geometry.collides(uprightChain, mover) ||
            (mover.y >= uprightChain.y &&
              mover.height + mover.y <= uprightChain.height + uprightChain.y &&
              mover.x < uprightChain.x &&
              mover.x + diff > uprightChain.x)
        )
      ) || movers.some(mover => mover.x < 0);
    if (!blockedLeft) {
      return 'left';
    }
  }
}

function moveSuperSection(section, superSection, to) {
  const itemsToUpdate = [];
  const uprights = superSection.filter(item =>
    productService.isType(item, ITEMS.UPRIGHT)
  );
  const diff = to.value - section.width;

  if (to.moveDirection === 'right') {
    itemsToUpdate.push(
      ...uprights
        .filter(upright => shouldMoveRight(upright, section))
        .map(item => {
          return { ...item, x: item.x + diff };
        })
        .sort((a, b) => (diff > 0 ? b.x - a.x : a.x - b.x))
    );
  } else if (to.moveDirection === 'left') {
    itemsToUpdate.push(
      ...uprights
        .filter(upright => shouldMoveLeft(upright, section))
        .map(item => {
          return { ...item, x: item.x - diff };
        })
        .sort((a, b) => (diff > 0 ? b.x - a.x : a.x - b.x))
    );
  }
  return itemsToUpdate;
}

function getChangeDimension(item) {
  if (productService.isSection(item)) {
    return 'width';
  }
}

export default {
  getMoveDirection,
  moveSuperSection,
  getChangeDimension,
};
